import {
  WebGLRenderTarget,
  OrthographicCamera,
  Scene,
  PlaneGeometry,
  Mesh,
  ShaderMaterial,
  HalfFloatType,
  Vector2,
  Vector3,
  Color,
  AdditiveBlending,
} from 'three';
import { isMobile } from '../_app/cuchillo/core/Basics';
import { Scroll } from '../_app/cuchillo/scroll/Scroll';

// Color corporatiu ('#rrggbb' o número 0xRRGGBB) → Vector3 en components sRGB [0,1]
// (SENSE conversió a lineal: el grade duotone treballa directament en sRGB, com els hex de marca).
function hexToSrgbVec3(hex) {
  const n = typeof hex === 'string' ? parseInt(hex.replace('#', ''), 16) : hex;
  return new Vector3(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255);
}

const DEG2RAD = Math.PI / 180;
const _prevClear = new Color(); // reutilitzat per guardar/restaurar el clearColor del renderer

// Coordenada Y del split (0..1). 0.5 → meitat superior amb la textura TOP (mar),
// meitat inferior amb la textura BOTTOM (2n render).
// seaFramingY: REENQUADRAMENT vertical del cel+mar (graus de pitch de càmera aplicat NOMÉS al 1r
// render). Mou l'horitzó amunt/avall dins el frame SENSE canviar la sensació frontal (que la dona
// seaY del mar). L'esfera/2n render NO s'inclina. 0 = sense canvi.
// parallax: desplaçament Y del CONTINGUT de les 2 textures segons el split. El contingut PUJA amb el
// split (split 0 → 0 ; 0.5 → parallax/2 amunt ; 1 → parallax amunt). Per defecte 0.5.
// seaFramingYScroll: offset ADDITIU del reenquadrament pel MOVE del billboard (0 = sense moviment; se
// suma a seaFramingY, que és el valor base animat per l'intro). Així el move no trepitja l'intro.
export const RENDER_SETTINGS = { split: 0, seaFramingY: 0, seaFramingYScroll: 0, parallax: 0.5, surfaceFade: 1 };

// Suavitzat de la LÍNIA de transició (split) perquè no quedi recta/definida: blur (mescla suau entre les
// dues regions) + noise (llindar ondulat). L'efecte es fon a 0 als extrems (split 0/1) via edgeFade →
// sense offset visible en repòs (i a 0/1 es fa un tall dur d'1 sola mostra, barat).
export const TRANSITION_SETTINGS = {
  fade: 0.023,      // amplada (a cada extrem split 0/1) on l'efecte s'esvaeix a 0. ↓ = línia ondulada més a prop dels extrems (menys zona recta)
  blur: 0.014,      // semi-amplada de la mescla (uv)
  noise: 0.008,     // amplitud de l'ondulació NOISE del llindar (uv) — més subtil
  noiseScale: 5,    // freqüència de l'ondulació noise al llarg de X
  sineAmp: 0.028,   // amplitud de l'ondulació SINUSOIDAL del llindar (uv) — més subtil
  sineFreq: 3,      // freqüència de la sinusoide al llarg de X
  animSpeed: 5,     // velocitat de l'animació de la forma (× progrés del split → es mou durant la transició)
  aberration: 0, // ABERRACIÓ cromàtica de la línia (desplaçament del llindar per canal R/B)
};

// NOISE del render GENERAL: BLUE NOISE (Interleaved Gradient Noise) ANIMAT, tintat, aplicat a tot el
// composite després del tone mapping. Gra repartit uniformement (sense grumolls, look de cinema) que
// s'anima segons `speed` (0 = estàtic). Mida/color/opacitat configurables. (Substitueix el white noise.)
export const NOISE_SETTINGS = {
  enabled: true,
  intensity: 0.1,   // opacitat del gra (0 = res)
  scale: 1.2,        // MIDA del gra en píxels (↑ = gra més gros; 1 ≈ per píxel = gra fi)
  speed: 0.2,       // ritme d'animació (pas per frame; 0 = estàtic, 1 = camp nou cada frame)
  fxBoost: 0.8,     // el gra forma part del FX: s'intensifica a la zona del FX (× FX radius). 0 = uniforme
  color: '#16214E',  // color/tint del gra (navy del fons underwater)
};

// BRANDING: color grade (duotone) del 1r render (cel + mar) cap a la paleta corporativa.
// Mapeja les zones fosques → dark i les clares → light, mantenint la modulació per hores
// (exposició/sol). strength = quant s'aplica; gamma = on cau el punt mig del degradat.
export const BRAND_SETTINGS = {
  strength: 0.88,     // 0 = escena original, 1 = duotone pur. Alt → paleta corporativa dominant.
  dark: '#010845',    // COLOR FOSC corporatiu → mar, cel (llevat del halo del sol), núvols.
  mid: '#013680',     // mig tons (barreja fosc↔clar) → tonalitats intermèdies (reflexos, ombres noise).
  light: '#0165bb',   // COLOR CLAR corporatiu → sol + el seu halo al cel, reflexos de l'aigua.
  gamma: 0.9,         // corba del degradat fosc→clar (↑ = més escena fosca, el clar només al sol)
  naturalPull: 0.0,   // 0 = sense matisos càlids d'albada/posta (paleta estable freda)
  // Temperatura (albada/posta càlides / nit freda). uTempMix el porta Scene per hora (warmth).
  warmth: 0.0,        // 0 = sense viratge de temperatura (paleta estable freda corporativa)
  warm: '#ffc27a',    // tint càlid (posta: taronja/vermell) — inactiu amb warmth 0
  cool: '#bcd0f2',    // tint fred de nit — inactiu amb warmth 0
};

// FONS UNDERWATER: degradat GRAN per scroll global. El fons del 2n render (clear color de rtBottom) va del
// color 'clear' (scroll 0, dalt de tot) al 'dark' (scroll màxim, final de pàgina). Es lerpa per CPU amb
// Scroll.progress (que Lenis ja manté i recalcula al resize) → cap cost extra a la GPU ni canvi de shader.
export const UNDERWATER_SETTINGS = {
  clear: '#16214E', // scroll 0 (el navy actual)
  dark:  '#0A0F2A', // scroll màxim (placeholder — ajusta'l al pane)
};

// FX del render GENERAL: aberració cromàtica radial + blur focal de disc, aplicats a TOT el
// composite (cel+mar i 2n render). Radials: nuls al centre, màxims a les cantonades. Configurable.
export const FX_SETTINGS = {
  enabled: true,
  radius: 0.23,       // on comença l'efecte (0 = des del centre; 1 = només cantonades extremes)
  // Aberració cromàtica
  abIntensity: 3,     // força global (0 = desactivada)
  abDisplace: 0.001,  // desplaçament radial per canal (uv)
  abDirectional: 0.1, // asimetria E/O (0 = simètric; dreta +, esquerra −)
  // Blur focal (disc/bokeh)
  blurIntensity: 0.27, // 0 = sense blur
  blurRadius: 0.041,  // radi màx del blur (uv) a les cantonades
};

// BLOOM (glow) NOMÉS dels godrays + oxigen particles (underwater). Es renderitzen aquestes 2 layers a un
// target dedicat (mitja resolució), es difumina (gaussiana separable, ping-pong) i s'afegeix ADDITIU a la
// regió underwater al composite. Configurable i activable des del pane (carpeta Render → Bloom).
export const BLOOM_SETTINGS = {
  enabled: false,
  // Quines layers underwater contribueixen al bloom (per veure combinacions) + intensitat de cada tipus
  // (cada layer es renderitza per separat i s'acumula escalada per la seva intensitat abans del blur):
  particles: true, particlesIntensity: 1.0,    // oxigen particles
  godrays: true, godraysIntensity: 1.0,      // godrays
  lightcircles: true, lightcirclesIntensity: 1.0, // cercles de llum (block_step)
  intensity: 0.9,     // guany GLOBAL del glow afegit (mestre; 0 = res)
  radius: 1.4,        // amplada del blur (× pas de texel a mitja res) → glow més ample
  iterations: 5,      // nº de passos H+V (més = glow més suau/ample)
};

const COMPOSITE_VERTEX = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Copia escalada (per acumular cada layer del bloom × la seva intensitat, amb blending additiu).
const BLOOM_COPY_FRAGMENT = /* glsl */ `
  precision highp float;
  uniform sampler2D tDiffuse;
  uniform float uScale;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(tDiffuse, vUv).rgb * uScale, 1.0); }
`;

// Blur gaussià separable (5 taps) per al bloom. uDir = direcció·radi en UV (H o V).
const BLOOM_BLUR_FRAGMENT = /* glsl */ `
  precision highp float;
  uniform sampler2D tDiffuse;
  uniform vec2 uDir;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(tDiffuse, vUv).rgb * 0.227027;
    c += texture2D(tDiffuse, vUv + uDir * 1.3846).rgb * 0.316216;
    c += texture2D(tDiffuse, vUv - uDir * 1.3846).rgb * 0.316216;
    c += texture2D(tDiffuse, vUv + uDir * 3.2308).rgb * 0.070270;
    c += texture2D(tDiffuse, vUv - uDir * 3.2308).rgb * 0.070270;
    gl_FragColor = vec4(c, 1.0);
  }
`;

// IMPORTANT: el renderer NO aplica tone mapping quan renderitza a un render target, i el Sky/Water
// de three són HDR (valors > 1). Per això els RT són HalfFloat (HDR lineal) i AQUÍ apliquem
// nosaltres el tone mapping ACES + l'exposició (dia/nit) i la conversió a sRGB.
// vUv.y=1 és a dalt, 0 a baix → vUv.y > split = regió superior = tTop (mar/cel).
const COMPOSITE_FRAGMENT = /* glsl */ `
  uniform sampler2D tTop;
  uniform sampler2D tBottom;
  uniform sampler2D uBloom;        // glow (godrays+particles difuminats), additiu a l'underwater
  uniform float uBloomIntensity;   // 0 = bloom desactivat
  uniform float split;
  uniform float uParallax; // desplaçament Y del contingut de les textures segons el split
  uniform float uExposure;
  uniform float uSurfaceFade; // fade del surface (1r render, cel+mar) a l'entrada. 1 = ple, 0 = negre
  uniform vec2 uTexel;
  uniform float uFxEnabled, uFxRadius;
  uniform float uAbIntensity, uAbDisplace, uAbDirectional;
  uniform float uBlurIntensity, uBlurRadius;
  uniform float uBrandStrength, uBrandGamma, uBrandHour; // uBrandHour: afluixa el grade per hora
  uniform vec3 uBrandDark, uBrandMid, uBrandLight;
  uniform float uWarmth, uTempMix;      // temperatura: dia càlid (uTempMix→1) / nit fred (→0)
  uniform vec3 uWarmTint, uCoolTint;
  uniform float uNoiseEnabled, uNoiseIntensity, uNoiseScale, uNoiseTime, uNoiseFxBoost;
  uniform vec3 uNoiseColor;
  // Línia de transició (tot × edgeFade → 0 als extrems). uLinePhase = split·animSpeed (anima amb el progrés).
  uniform float uEdgeFade, uSplitBlur, uSplitNoise, uSplitFreq;
  uniform float uLineSineAmp, uLineSineFreq, uLinePhase, uLineAb;
  varying vec2 vUv;

  #define BLUR_TAPS 16
  const float GOLDEN_ANGLE = 2.39996323; // 137.5°

  vec3 aces(vec3 x) {
    return clamp((x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14), 0.0, 1.0);
  }
  vec3 lin2srgb(vec3 c) {
    return pow(clamp(c, 0.0, 1.0), vec3(1.0 / 2.2));
  }

  // Grade corporatiu (tricolor): la lluminositat de l'escena indexa un degradat de 3 parades
  // dark→mid→light. El mig (mar) queda distint del fosc (cel) → el mar es veu també de nit.
  // Es treballa en sRGB (mateixa base que els hex corporatius). strength en controla la mescla.
  vec3 brandGrade(vec3 c) {
    float luma = dot(c, vec3(0.299, 0.587, 0.114));
    luma = pow(clamp(luma, 0.0, 1.0), uBrandGamma);
    vec3 ramp = luma < 0.5
      ? mix(uBrandDark, uBrandMid, luma * 2.0)
      : mix(uBrandMid, uBrandLight, (luma - 0.5) * 2.0);
    vec3 col = mix(c, ramp, uBrandStrength * uBrandHour);
    // Temperatura: de dia vira càlid, de nit fred (multiplicatiu, subtil segons uWarmth).
    vec3 tint = mix(uCoolTint, uWarmTint, uTempMix);
    return col * mix(vec3(1.0), tint, uWarmth);
  }

  // Soroll 1D suau (value noise + fbm) per ondular la línia de transició (que no quedi recta).
  float _hbnd(float n) { return fract(sin(n) * 43758.5453123); }
  float _vnbnd(float x) { float i = floor(x), f = fract(x); float u = f * f * (3.0 - 2.0 * f); return mix(_hbnd(i), _hbnd(i + 1.0), u); }
  float _fbnd(float x) { float v = 0.0, a = 0.5; for (int k = 0; k < 3; k++) { v += a * _vnbnd(x); x *= 2.0; a *= 0.5; } return v; }

  // Color d'UNA regió (amb el seu parallax): topRegion>0.5 → 1r render (cel/mar + grade); si no → 2n render.
  // PARALLAX: la textura es mostreja desplaçada en Y (offsets complementaris: render 1 correcte a split 0;
  // render 2 correcte a split 1).
  vec3 _regionAt(vec2 uv, float topRegion) {
    if (topRegion > 0.5) {
      vec2 s = vec2(uv.x, uv.y - uParallax * split);
      return brandGrade(lin2srgb(aces(texture2D(tTop, s).rgb * uExposure))) * uSurfaceFade;
    }
    vec2 s = vec2(uv.x, uv.y - uParallax * (split - 1.0));
    // Underwater + BLOOM additiu (godrays+particles) abans del tone mapping → glow HDR agradable.
    vec3 c = texture2D(tBottom, s).rgb + texture2D(uBloom, s).rgb * uBloomIntensity;
    return lin2srgb(aces(c));
  }

  // Color FINAL compostat. La LÍNIA de transició (split) es suavitza amb NOISE (llindar ondulat) + BLUR
  // (mescla suau entre les dues regions). uSplitBlur/uSplitNoise ja venen escalats per l'edgeFade (0 a
  // split 0/1) → a repòs (0/1) fem TALL DUR d'1 sola mostra (barat i sense offset visible als extrems).
  vec3 sceneAt(vec2 uv) {
    // Repòs (edgeFade 0, split 0/1): tall dur d'1 sola mostra (barat, sense offset visible als extrems).
    if (uEdgeFade <= 0.0001) {
      return _regionAt(uv, uv.y > split ? 1.0 : 0.0);
    }
    // Forma del llindar: SINUSOIDE + NOISE, animats amb el progrés del split (uLinePhase).
    float wob = sin(uv.x * uLineSineFreq + uLinePhase) * uLineSineAmp
              + (_fbnd(uv.x * uSplitFreq + uLinePhase) - 0.5) * uSplitNoise;
    float boundary = split + wob;
    vec3 bot = _regionAt(uv, 0.0);
    vec3 top = _regionAt(uv, 1.0);
    // BLUR (mescla suau) + ABERRACIÓ cromàtica (llindar desplaçat per canal R/B).
    float tR = smoothstep(boundary + uLineAb - uSplitBlur, boundary + uLineAb + uSplitBlur, uv.y);
    float tG = smoothstep(boundary - uSplitBlur, boundary + uSplitBlur, uv.y);
    float tB = smoothstep(boundary - uLineAb - uSplitBlur, boundary - uLineAb + uSplitBlur, uv.y);
    return vec3(mix(bot, top, tR).r, mix(bot, top, tG).g, mix(bot, top, tB).b);
  }

  // Aberració cromàtica radial: offsets per canal (R enfora, B endins), nuls al centre (via edge),
  // amb biaix direccional horitzontal (dreta +, esquerra −).
  void fxAberrationOffsets(vec2 uv, vec2 center, float edge, out vec2 offR, out vec2 offB) {
    vec2 dir = uv - center;
    float len = length(dir);
    vec2 radial = len > 1e-5 ? dir / len : vec2(0.0);
    float amt = uAbDisplace * uAbIntensity * edge;
    float dirX = sign(uv.x - center.x) * amt * uAbDirectional;
    offR =  radial * amt + vec2(dirX, 0.0);
    offB = -radial * amt - vec2(dirX, 0.0);
  }

  // Blur fotogràfic de disc (bokeh de lent) sobre l'ESCENA COMPOSTADA (no una sola textura) → així
  // el blur funciona a TOT el render i creua el split correctament. Espiral d'angle auri.
  vec3 fxDiscBlur(vec2 uv, float radius) {
    float r = radius * uBlurIntensity;
    if (r < 0.0005) return sceneAt(uv);
    float aspect = uTexel.y / uTexel.x; // disc circular en píxels
    vec3 sum = sceneAt(uv);
    float total = 1.0;
    for (int i = 0; i < BLUR_TAPS; i++) {
      float fi = float(i);
      float ang = fi * GOLDEN_ANGLE;
      float rad = sqrt((fi + 0.5) / float(BLUR_TAPS)) * r;
      vec2 dir = vec2(cos(ang), sin(ang));
      vec2 off = vec2(dir.x, dir.y * aspect) * rad;
      sum += sceneAt(uv + off);
      total += 1.0;
    }
    return sum / total;
  }

  // FX GENERAL a tot el composite: factor radial 'edge' (0 centre → 1 cantonada), aberració + blur
  // per canal. Al centre (edge=0) retorna l'escena directa → barat.
  vec3 fxComposite(vec2 uv) {
    float distN = length(uv - vec2(0.5)) / 0.70710678; // 0 centre → 1 cantonada
    float edge = smoothstep(uFxRadius, 1.0, distN);
    if (edge <= 0.0) return sceneAt(uv);
    vec2 oR, oB;
    fxAberrationOffsets(uv, vec2(0.5), edge, oR, oB);
    float br = uBlurRadius * edge;
    float r = fxDiscBlur(uv + oR, br).r;
    float g = fxDiscBlur(uv,      br).g;
    float b = fxDiscBlur(uv + oB, br).b;
    return vec3(r, g, b);
  }

  // ── Blue noise (gra de cinema): Interleaved Gradient Noise (Jiménez) — dither repartit uniformement
  //    (sense grumolls com el white noise), barat i sense textura. S'anima amb la successió àuria. ──────
  float ign(vec2 p) { return fract(52.9829189 * fract(dot(p, vec2(0.06711056, 0.00583715)))); }
  float hash11(float n) { return fract(sin(n) * 43758.5453123); }

  void main() {
    // Durant la transició de màscara (uEdgeFade>0) NO fem fxComposite: el disc-blur crida
    // sceneAt ~50×/píxel i cada sceneAt mostreja+gradeja les 2 regions → crash de FPS.
    // La línia suau (noise+blur del split) ja dóna la màscara; el FX torna a repòs (0/1).
    vec3 srgb = (uFxEnabled > 0.5 && uEdgeFade <= 0.0001) ? fxComposite(vUv) : sceneAt(vUv);

    // BLUE NOISE (IGN): gra repartit uniformement (agradable, sense grumolls). uNoiseScale = mida del gra
    // en px; s'anima RE-SEMBRANT cada frame (un camp aleatori nou per frame, SENSE moviment direccional/deriva)
    // segons uNoiseTime (speed). speed 0 = estàtic (fr es queda a 0).
    if (uNoiseEnabled > 0.5) {
      vec2 cell = floor((vUv / uTexel) / max(0.5, uNoiseScale));
      float fr = mod(floor(uNoiseTime), 1024.0);                 // índex de frame (avança segons speed)
      vec2 seed = vec2(hash11(fr), hash11(fr + 41.3)) * 1024.0;  // seed random per frame → camp nou, SENSE deriva
      float n = ign(cell + seed);
      // El gra forma part del FX: s'intensifica cap a la zona afectada (MATEIX perfil radial que el FX).
      float distN = length(vUv - vec2(0.5)) / 0.70710678;        // 0 centre → 1 cantonada
      float fxEdge = smoothstep(uFxRadius, 1.0, distN);
      float amt = uNoiseIntensity * (1.0 + fxEdge * uNoiseFxBoost);
      srgb += uNoiseColor * (n - 0.5) * amt * 2.0;
    }

    // alpha 1 → les zones sense geometria del 2n render mostren el clearColor, no la pàgina.
    gl_FragColor = vec4(clamp(srgb, 0.0, 1.0), 1.0);
  }
`;

// Double render: renderitza l'escena dos cops (per layers de càmera) a dos render targets HDR
// i els composa a pantalla amb un split horitzontal + tone mapping + FX del 2n render.
export default class SceneCompositor {
  constructor() {
    this.rtTop = null;
    this.rtBottom = null;
    this.material = null;
    this.quadScene = null;
    this.quadCamera = null;
    // Fons underwater (degradat per scroll): colors extrems (cache) + color reusable per frame.
    this._seaBgClear = new Color();
    this._seaBgDark = new Color();
    this._seaBg = new Color();
    this.applyUnderwaterBg();
  }

  init(renderer, width, height) {
    this.renderer = renderer; // referència (útil per debug/tests)
    const opts = { type: HalfFloatType }; // HDR lineal (conserva els valors > 1 del Sky)
    this.rtTop = new WebGLRenderTarget(width, height, opts);
    this.rtBottom = new WebGLRenderTarget(width, height, opts);

    // ── Bloom: targets a MITJA resolució (font godrays+particles + ping-pong del blur) ──
    this._bloomW = Math.max(1, Math.floor(width / 2));
    this._bloomH = Math.max(1, Math.floor(height / 2));
    this.rtBloomTemp = new WebGLRenderTarget(this._bloomW, this._bloomH, opts); // 1 layer aïllada
    this.rtBloomSrc = new WebGLRenderTarget(this._bloomW, this._bloomH, opts);  // acumulador de layers
    this.rtBloomA = new WebGLRenderTarget(this._bloomW, this._bloomH, opts);
    this.rtBloomB = new WebGLRenderTarget(this._bloomW, this._bloomH, opts);
    this._bloomMat = new ShaderMaterial({
      uniforms: { tDiffuse: { value: null }, uDir: { value: new Vector2() } },
      vertexShader: COMPOSITE_VERTEX, fragmentShader: BLOOM_BLUR_FRAGMENT,
      depthTest: false, depthWrite: false, toneMapped: false,
    });
    this._bloomScene = new Scene();
    this._bloomScene.add(new Mesh(new PlaneGeometry(2, 2), this._bloomMat));
    // Copy-add: acumula cada layer × intensitat a rtBloomSrc (blending additiu).
    this._bloomCopyMat = new ShaderMaterial({
      uniforms: { tDiffuse: { value: null }, uScale: { value: 1 } },
      vertexShader: COMPOSITE_VERTEX, fragmentShader: BLOOM_COPY_FRAGMENT,
      depthTest: false, depthWrite: false, toneMapped: false, blending: AdditiveBlending, transparent: true,
    });
    this._bloomCopyScene = new Scene();
    this._bloomCopyScene.add(new Mesh(new PlaneGeometry(2, 2), this._bloomCopyMat));

    const f = FX_SETTINGS;
    this.material = new ShaderMaterial({
      uniforms: {
        tTop: { value: this.rtTop.texture },
        tBottom: { value: this.rtBottom.texture },
        uBloom: { value: this.rtBloomB.texture },
        uBloomIntensity: { value: 0 },
        split: { value: RENDER_SETTINGS.split },
        uParallax: { value: RENDER_SETTINGS.parallax },
        uExposure: { value: 1 },
        uSurfaceFade: { value: 1 },
        uTexel: { value: new Vector2(1 / width, 1 / height) },
        uFxEnabled: { value: f.enabled ? 1 : 0 },
        uFxRadius: { value: f.radius },
        uAbIntensity: { value: f.abIntensity },
        uAbDisplace: { value: f.abDisplace },
        uAbDirectional: { value: f.abDirectional },
        uBlurIntensity: { value: isMobile ? 0 : f.blurIntensity }, // a mobile: sense blur radial (postpro)
        uBlurRadius: { value: f.blurRadius },
        uBrandStrength: { value: BRAND_SETTINGS.strength },
        uBrandGamma: { value: BRAND_SETTINGS.gamma },
        uBrandHour: { value: 1 },
        uBrandDark: { value: hexToSrgbVec3(BRAND_SETTINGS.dark) },
        uBrandMid: { value: hexToSrgbVec3(BRAND_SETTINGS.mid) },
        uBrandLight: { value: hexToSrgbVec3(BRAND_SETTINGS.light) },
        uWarmth: { value: BRAND_SETTINGS.warmth },
        uTempMix: { value: 0 },
        uWarmTint: { value: hexToSrgbVec3(BRAND_SETTINGS.warm) },
        uCoolTint: { value: hexToSrgbVec3(BRAND_SETTINGS.cool) },
        uNoiseEnabled: { value: NOISE_SETTINGS.enabled ? 1 : 0 },
        uNoiseIntensity: { value: NOISE_SETTINGS.intensity },
        uNoiseScale: { value: NOISE_SETTINGS.scale },
        uNoiseTime: { value: 0 },
        uNoiseFxBoost: { value: NOISE_SETTINGS.fxBoost },
        uNoiseColor: { value: hexToSrgbVec3(NOISE_SETTINGS.color) },
        uEdgeFade: { value: 0 }, uSplitBlur: { value: 0 }, uSplitNoise: { value: 0 }, uSplitFreq: { value: TRANSITION_SETTINGS.noiseScale },
        uLineSineAmp: { value: 0 }, uLineSineFreq: { value: TRANSITION_SETTINGS.sineFreq }, uLinePhase: { value: 0 }, uLineAb: { value: 0 },
      },
      vertexShader: COMPOSITE_VERTEX,
      fragmentShader: COMPOSITE_FRAGMENT,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    });

    this.quadCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.quadScene = new Scene();
    this.quadScene.add(new Mesh(new PlaneGeometry(2, 2), this.material));
  }

  // Renderitza topLayers → rtTop, bottomLayers → rtBottom, i composa a pantalla.
  // showTop/showBottom: salta el pass ocult (a split 0 el composite NO mostreja tBottom; a split 1, tTop)
  // → estalvia un render pass sencer quan una regió no es veu. Durant la transició, tots dos true.
  render(renderer, scene, camera, topLayers, bottomLayers, showTop = true, showBottom = true, bloomItems = null) {
    if (!this.material) return;

    // Guardem TOT l'estat del renderer/càmera que tocarem (perquè el render sigui AUTO-CONTINGUT
    // i no depengui ni contamini altres sistemes: BG/paletes, WebGLSketch, raycaster…).
    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    const prevLayers = camera.layers.mask;
    const prevRotX = camera.rotation.x;
    renderer.getClearColor(_prevClear);
    const prevClearAlpha = renderer.getClearAlpha();

    // FONS NEGRE OPAC als render targets: immune a canvis externs del clearColor (el sistema de
    // paletes BG el canvia durant l'scroll → abans el 2n render sortia amb fons blanc / en negatiu).
    renderer.autoClear = true;
    renderer.setClearColor(0x000000, 1);

    const pitch = (RENDER_SETTINGS.seaFramingY + RENDER_SETTINGS.seaFramingYScroll) * DEG2RAD;

    // ── Pass TOP → rtTop (reenquadrament vertical del cel+mar via pitch de càmera) ──
    if (showTop) {
      camera.layers.set(topLayers[0]);
      for (let i = 1; i < topLayers.length; i++) camera.layers.enable(topLayers[i]);
      camera.rotation.x = prevRotX + pitch;
      renderer.setRenderTarget(this.rtTop);
      renderer.clear();
      renderer.render(scene, camera);
      camera.rotation.x = prevRotX; // el 2n render (esfera) NO s'inclina
    }

    // ── Pass BOTTOM → rtBottom (fons underwater dedicat, no negre) ──
    if (showBottom) {
      camera.layers.set(bottomLayers[0]);
      for (let i = 1; i < bottomLayers.length; i++) camera.layers.enable(bottomLayers[i]);
      // Fons underwater = degradat GRAN per scroll: clear (dalt) → dark (final de pàgina). Lerp per CPU.
      const t = Math.max(0, Math.min(1, Scroll.progress ?? 0));
      this._seaBg.lerpColors(this._seaBgClear, this._seaBgDark, t);
      renderer.setClearColor(this._seaBg, 1); // fons del 2n render (underwater)
      renderer.setRenderTarget(this.rtBottom);
      renderer.clear();
      renderer.render(scene, camera);
    }

    // ── Bloom (godrays/particles/lightcircles, cada un × la seva intensitat) → rtBloomB ──
    const B = BLOOM_SETTINGS;
    let bloomOn = false;
    const items = (bloomItems || []).filter((it) => it.intensity > 0);
    if (showBottom && B.enabled && B.intensity > 0 && items.length) {
      // Acumula cada layer PER SEPARAT a rtBloomSrc, escalada per la seva intensitat (blending additiu).
      renderer.setClearColor(0x000000, 1);
      renderer.setRenderTarget(this.rtBloomSrc);
      renderer.clear();
      renderer.autoClear = false; // acumular sense esborrar entre layers
      for (let i = 0; i < items.length; i++) {
        camera.layers.set(items[i].layer);
        renderer.setRenderTarget(this.rtBloomTemp);
        renderer.clear();
        renderer.render(scene, camera);
        this._bloomCopyMat.uniforms.tDiffuse.value = this.rtBloomTemp.texture;
        this._bloomCopyMat.uniforms.uScale.value = items[i].intensity;
        renderer.setRenderTarget(this.rtBloomSrc);
        renderer.render(this._bloomCopyScene, this.quadCamera); // additiu, sense clear
      }
      renderer.autoClear = true;
      // Blur ping-pong: cada iteració H (→rtBloomA) i V (→rtBloomB); la següent llegeix de rtBloomB.
      const tx = B.radius / this._bloomW, ty = B.radius / this._bloomH;
      let read = this.rtBloomSrc.texture;
      const iters = Math.max(1, Math.floor(B.iterations));
      for (let i = 0; i < iters; i++) {
        this._bloomMat.uniforms.tDiffuse.value = read;
        this._bloomMat.uniforms.uDir.value.set(tx, 0);
        renderer.setRenderTarget(this.rtBloomA);
        renderer.clear();
        renderer.render(this._bloomScene, this.quadCamera);
        this._bloomMat.uniforms.tDiffuse.value = this.rtBloomA.texture;
        this._bloomMat.uniforms.uDir.value.set(0, ty);
        renderer.setRenderTarget(this.rtBloomB);
        renderer.clear();
        renderer.render(this._bloomScene, this.quadCamera);
        read = this.rtBloomB.texture;
      }
      this.material.uniforms.uBloom.value = this.rtBloomB.texture;
      bloomOn = true;
    }

    // ── Composite → pantalla ──────────────────────────────────────────────────
    camera.layers.mask = prevLayers; // restaurem els layers (NO enableAll → no contamina)
    this.material.uniforms.uBloomIntensity.value = bloomOn ? B.intensity : 0;
    renderer.setRenderTarget(null);
    this.material.uniforms.split.value = RENDER_SETTINGS.split;
    this.material.uniforms.uParallax.value = RENDER_SETTINGS.parallax;
    this.material.uniforms.uSurfaceFade.value = RENDER_SETTINGS.surfaceFade;
    // Línia de transició: edgeFade 0 als extrems (split 0/1) → 1 al mig → l'efecte no assoma a repòs.
    // Les AMPLITUDS es multipliquen per edgeFade; les escales (freq/width) no. uLinePhase = split·animSpeed.
    const _ss = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
    const sp = RENDER_SETTINGS.split, T = TRANSITION_SETTINGS, u = this.material.uniforms;
    const _ef = _ss(0, T.fade, sp) * (1 - _ss(1 - T.fade, 1, sp));
    u.uEdgeFade.value = _ef;
    u.uSplitBlur.value = T.blur * _ef;
    u.uSplitNoise.value = T.noise * _ef;
    u.uSplitFreq.value = T.noiseScale;
    u.uLineSineAmp.value = T.sineAmp * _ef;
    u.uLineSineFreq.value = T.sineFreq;
    u.uLinePhase.value = sp * T.animSpeed;   // animació de la forma lligada al progrés del split
    u.uLineAb.value = T.aberration * _ef;
    // FX disc-blur només en repòs (split 0/1). Durant la màscara és massa car (veure main del shader).
    u.uFxEnabled.value = (FX_SETTINGS.enabled && _ef <= 0.0001) ? 1 : 0;
    // White noise animat: acumulem un índex de frame (pas = speed; 0 = gra estàtic).
    this._noiseFrame = (this._noiseFrame || 0) + NOISE_SETTINGS.speed;
    this.material.uniforms.uNoiseTime.value = this._noiseFrame;
    renderer.render(this.quadScene, this.quadCamera);

    // Restaurem l'estat del renderer per als altres sistemes.
    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAutoClear;
    renderer.setClearColor(_prevClear, prevClearAlpha);
  }

  setSplit(v) {
    RENDER_SETTINGS.split = v;
    if (this.material) this.material.uniforms.split.value = v;
  }

  // Reenquadrament vertical del 1r render (graus de pitch). render() ho llegeix de RENDER_SETTINGS.
  setSeaFraming(v) { RENDER_SETTINGS.seaFramingY = v; }

  // Quantitat de parallax (desplaçament Y del contingut de les textures segons el split).
  setParallax(v) {
    RENDER_SETTINGS.parallax = v;
    if (this.material) this.material.uniforms.uParallax.value = v;
  }

  // Exposició del cel+mar (dia clar / nit fosca). La controla el sistema d'hores de Scene.
  setExposure(v) {
    if (this.material) this.material.uniforms.uExposure.value = v;
  }

  // Empeny FX_SETTINGS als uniforms (des del debug, en viu).
  applyFx() {
    if (!this.material) return;
    const u = this.material.uniforms, f = FX_SETTINGS;
    u.uFxEnabled.value = f.enabled ? 1 : 0;
    u.uFxRadius.value = f.radius;
    u.uAbIntensity.value = f.abIntensity;
    u.uAbDisplace.value = f.abDisplace;
    u.uAbDirectional.value = f.abDirectional;
    u.uBlurIntensity.value = isMobile ? 0 : f.blurIntensity; // a mobile: sense blur radial (postpro)
    u.uBlurRadius.value = f.blurRadius;
  }

  // Empeny BRAND_SETTINGS als uniforms (des del debug, en viu).
  applyBrand() {
    if (!this.material) return;
    const u = this.material.uniforms, b = BRAND_SETTINGS;
    u.uBrandStrength.value = b.strength;
    u.uBrandGamma.value = b.gamma;
    u.uBrandDark.value.copy(hexToSrgbVec3(b.dark));
    u.uBrandMid.value.copy(hexToSrgbVec3(b.mid));
    u.uBrandLight.value.copy(hexToSrgbVec3(b.light));
    u.uWarmth.value = b.warmth;
    u.uWarmTint.value.copy(hexToSrgbVec3(b.warm));
    u.uCoolTint.value.copy(hexToSrgbVec3(b.cool));
  }

  // Colors del degradat underwater (des del debug, en viu). No toca uniforms: només refresca la cache.
  applyUnderwaterBg() {
    this._seaBgClear.set(UNDERWATER_SETTINGS.clear);
    this._seaBgDark.set(UNDERWATER_SETTINGS.dark);
  }

  // Empeny NOISE_SETTINGS als uniforms (des del debug, en viu).
  applyNoise() {
    if (!this.material) return;
    const u = this.material.uniforms, n = NOISE_SETTINGS;
    u.uNoiseEnabled.value = n.enabled ? 1 : 0;
    u.uNoiseIntensity.value = n.intensity;
    u.uNoiseScale.value = n.scale;
    u.uNoiseFxBoost.value = n.fxBoost;
    u.uNoiseColor.value.copy(hexToSrgbVec3(n.color));
  }

  // Mescla de temperatura per hora (0 = fred/nit, 1 = càlid/dia). La porta Scene.
  setTemperature(v) {
    if (this.material) this.material.uniforms.uTempMix.value = v;
  }

  // Afluixa el grade per hora (1 = grade complet; <1 a albada/posta → matisos naturals). Scene.
  setBrandHour(v) {
    if (this.material) this.material.uniforms.uBrandHour.value = v;
  }

  resize(width, height) {
    if (this.rtTop) this.rtTop.setSize(width, height);
    if (this.rtBottom) this.rtBottom.setSize(width, height);
    this._bloomW = Math.max(1, Math.floor(width / 2));
    this._bloomH = Math.max(1, Math.floor(height / 2));
    if (this.rtBloomTemp) this.rtBloomTemp.setSize(this._bloomW, this._bloomH);
    if (this.rtBloomSrc) this.rtBloomSrc.setSize(this._bloomW, this._bloomH);
    if (this.rtBloomA) this.rtBloomA.setSize(this._bloomW, this._bloomH);
    if (this.rtBloomB) this.rtBloomB.setSize(this._bloomW, this._bloomH);
    if (this.material) this.material.uniforms.uTexel.value.set(1 / width, 1 / height);
  }

  dispose() {
    if (this.rtTop) { this.rtTop.dispose(); this.rtTop = null; }
    if (this.rtBottom) { this.rtBottom.dispose(); this.rtBottom = null; }
    if (this.rtBloomTemp) { this.rtBloomTemp.dispose(); this.rtBloomTemp = null; }
    if (this.rtBloomSrc) { this.rtBloomSrc.dispose(); this.rtBloomSrc = null; }
    if (this.rtBloomA) { this.rtBloomA.dispose(); this.rtBloomA = null; }
    if (this.rtBloomB) { this.rtBloomB.dispose(); this.rtBloomB = null; }
    if (this._bloomMat) { this._bloomMat.dispose(); this._bloomMat = null; }
    if (this._bloomCopyMat) { this._bloomCopyMat.dispose(); this._bloomCopyMat = null; }
    if (this.quadScene) {
      this.quadScene.traverse((o) => { if (o.isMesh) o.geometry?.dispose(); });
      this.quadScene = null;
    }
    if (this.material) { this.material.dispose(); this.material = null; }
    this.quadCamera = null;
  }
}
